import { useEffect, useMemo, useState } from 'react';
import { CameraCapture } from './CameraCapture';
import { LoadingScan } from './LoadingScan';
import { ApiError, parseMenu, saveMenu } from '../lib/api';
import { ErrorCodes } from '../../shared/api';
import { extraCompressForUpload } from '../lib/compress';
import { saveMenuLocal } from '../lib/persist';
import { useSettings } from '../lib/store';
import type { Menu } from '../../shared/menu';

interface AddPagesFlowProps {
  previousMenuId: string;
  // Surfaces a server-resolved success: the new menuId and the freshly-parsed
  // menu. The parent (MenuScreen) handles cart/rename migration before
  // navigating, so we hand the menu back rather than navigating ourselves.
  onSuccess: (args: { menuId: string; menu: Menu; modelUsed: string }) => void;
  onCancel: () => void;
}

// Mirrors ScanScreen's submit/error/loading states but always sends
// previousMenuId so the server combines pages and produces a new menuId.
// MAX_PAGES is enforced server-side; if the user adds more new photos than
// would fit alongside the originals, the server returns a clear error.
export function AddPagesFlow({ previousMenuId, onSuccess, onCancel }: AddPagesFlowProps) {
  const targetLanguage = useSettings((s) => s.targetLanguage);
  const [submitting, setSubmitting] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[] | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const previews = useMemo(() => pendingFiles?.map((f) => URL.createObjectURL(f)) ?? [], [pendingFiles]);
  useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p)), [previews]);

  const onSubmit = async (files: File[]) => {
    setSubmitting(true);
    setPendingFiles(files);
    setErrorMessage(null);

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setErrorMessage('Offline. Connect to add a page.');
      setSubmitting(false);
      setPendingFiles(null);
      return;
    }

    try {
      let result;
      try {
        result = await parseMenu({ files, targetLanguage, previousMenuId });
      } catch (err) {
        if (err instanceof ApiError && err.code === ErrorCodes.IMAGE_TOO_LARGE) {
          const smaller = await Promise.all(files.map((f) => extraCompressForUpload(f)));
          result = await parseMenu({ files: smaller, targetLanguage, previousMenuId });
        } else {
          throw err;
        }
      }
      await saveMenuLocal(result.menuId, result.menu, result.modelUsed);
      void saveMenu(result.menuId).catch(() => {});
      onSuccess({ menuId: result.menuId, menu: result.menu, modelUsed: result.modelUsed });
    } catch (err) {
      const wentOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
      const message = wentOffline
        ? 'Offline. Connect to add a page.'
        : err instanceof ApiError
          ? err.message
          : 'Something went wrong. Try again.';
      setErrorMessage(message);
      setSubmitting(false);
      setPendingFiles(null);
    }
  };

  if (submitting && pendingFiles) {
    return <LoadingScan previews={previews} />;
  }

  return (
    <CameraCapture
      onSubmit={onSubmit}
      onCancel={onCancel}
      busy={submitting}
      errorMessage={errorMessage}
    />
  );
}
